import React, { useState, useEffect, useCallback } from 'react';
import { Layers, Plus, Edit2, Trash2, X, Search, Loader2, ChevronRight, Check, HelpCircle, ChevronDown, ArrowUp, ArrowDown, GripVertical } from 'lucide-react';
import { reconRulesApi } from '../../services/api';
import DataTable from '../../components/DataTable';
import { mockRules, mockRulesets, mockRulesetDetail } from './mockData';

const HowTo = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-medium text-blue-800">
          <HelpCircle className="w-4 h-4" /> How to use Rulesets
        </span>
        <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-blue-900 space-y-3">
          <div>
            <p className="font-semibold mb-1">What are Rulesets?</p>
            <p>Rulesets are named collections of rules grouped together for a specific validation purpose. For example, an "Eligibility Inbound" ruleset might include rules for row count, required fields, and duplicate checks.</p>
          </div>
          <div>
            <p className="font-semibold mb-1">Steps</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li><strong>Create a Ruleset</strong> - Click "Add Ruleset" and provide a unique code and name (e.g., <code className="bg-blue-100 px-1 rounded">eligibility_inbound_v1</code>).</li>
              <li><strong>Assign Rules</strong> - Click "Manage Rules" on a ruleset to select which rules to include. Check/uncheck rules from the available list.</li>
              <li><strong>Set Order</strong> - Rules execute in the order they are assigned. The order determines which validations run first.</li>
              <li><strong>Attach to Process</strong> - Once configured, go to the Processes page to attach this ruleset to a file processing job.</li>
            </ol>
          </div>
          <div>
            <p className="font-semibold mb-1">Tips</p>
            <p>Create separate rulesets for inbound vs outbound validations. You can reuse the same ruleset across multiple processes if they share the same validation logic.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default function ReconRulesets() {
  const [rulesets, setRulesets] = useState([]);
  const [allRules, setAllRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showRulesPanel, setShowRulesPanel] = useState(null); // rulesetId
  const [editingRuleset, setEditingRuleset] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ruleset_code: '', ruleset_name: '', description: '', is_active: true });
  const [selectedRules, setSelectedRules] = useState([]); // [{rule_id, sort_order, is_required}]
  const [ruleSearch, setRuleSearch] = useState('');

  const loadData = useCallback(async () => {
    try {
      let rsData, rlData;
      try {
        const [rulesetsRes, rulesRes] = await Promise.all([
          reconRulesApi.listRulesets(),
          reconRulesApi.listRules(),
        ]);
        rsData = rulesetsRes.data;
        rlData = rulesRes.data;
      } catch { rsData = []; rlData = []; }
      setRulesets(rsData && rsData.length > 0 ? rsData : mockRulesets);
      setAllRules((rlData && rlData.length > 0 ? rlData : mockRules).filter(r => r.is_active));
    } catch (err) {
      console.error('Failed to load rulesets:', err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = rulesets.filter(rs =>
    !search ||
    rs.ruleset_code?.toLowerCase().includes(search.toLowerCase()) ||
    rs.ruleset_name?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingRuleset(null);
    setForm({ ruleset_code: '', ruleset_name: '', description: '', is_active: true });
    setShowModal(true);
  };

  const openEdit = (rs) => {
    setEditingRuleset(rs);
    setForm({ ruleset_code: rs.ruleset_code, ruleset_name: rs.ruleset_name, description: rs.description || '', is_active: rs.is_active });
    setShowModal(true);
  };

  const openRulesPanel = async (rs) => {
    try {
      let rulesData;
      try {
        const res = await reconRulesApi.getRuleset(rs.ruleset_id);
        rulesData = res.data.rules || [];
      } catch {
        rulesData = mockRulesetDetail[rs.ruleset_id] || [];
      }
      const existing = (rulesData).map((r, i) => ({
        rule_id: r.rule_id, sort_order: r.sort_order || i, is_required: r.is_required !== false,
        override_params: r.override_params || '',
      }));
      setSelectedRules(existing);
      setShowRulesPanel(rs);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingRuleset) {
        await reconRulesApi.updateRuleset(editingRuleset.ruleset_id, form);
      } else {
        await reconRulesApi.createRuleset(form);
      }
      setShowModal(false);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (rs) => {
    if (!confirm(`Deactivate ruleset "${rs.ruleset_name}"?`)) return;
    try {
      await reconRulesApi.deleteRuleset(rs.ruleset_id);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const addRule = (ruleId) => {
    if (selectedRules.some(r => r.rule_id === ruleId)) return;
    setSelectedRules(prev => [...prev, { rule_id: ruleId, sort_order: prev.length + 1, is_required: true, override_params: '' }]);
  };

  const removeRule = (ruleId) => {
    setSelectedRules(prev => prev.filter(r => r.rule_id !== ruleId).map((r, i) => ({ ...r, sort_order: i + 1 })));
  };

  const moveRule = (index, direction) => {
    setSelectedRules(prev => {
      const arr = [...prev];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= arr.length) return prev;
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr.map((r, i) => ({ ...r, sort_order: i + 1 }));
    });
  };

  const filteredBankRules = allRules.filter(r => {
    if (selectedRules.some(s => s.rule_id === r.rule_id)) return false;
    if (!ruleSearch) return true;
    return r.rule_name?.toLowerCase().includes(ruleSearch.toLowerCase()) ||
      r.rule_code?.toLowerCase().includes(ruleSearch.toLowerCase()) ||
      r.rule_type?.toLowerCase().includes(ruleSearch.toLowerCase());
  });

  const saveRuleAssignment = async () => {
    setSaving(true);
    try {
      await reconRulesApi.updateRulesetRules(showRulesPanel.ruleset_id, selectedRules);
      setShowRulesPanel(null);
      setLoading(true);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { key: 'ruleset_code', label: 'Code' },
    { key: 'ruleset_name', label: 'Name' },
    { key: 'description', label: 'Description', render: (val) => <span className="text-gray-500 text-xs">{val || '-'}</span> },
    { key: 'rule_count', label: 'Rules', format: 'number' },
    { key: 'is_active', label: 'Active', render: (val) =>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${val ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
        {val ? 'Active' : 'Inactive'}
      </span>
    },
    { key: 'actions', label: 'Actions', sortable: false, render: (_, row) => (
      <div className="flex gap-2">
        <button onClick={() => openRulesPanel(row)} className="text-blue-600 hover:text-blue-800 text-xs px-2 py-1 bg-blue-50 rounded">Manage Rules</button>
        <button onClick={() => openEdit(row)} className="text-amber-600 hover:text-amber-800"><Edit2 className="w-4 h-4" /></button>
        <button onClick={() => handleDelete(row)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Layers className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reconciliation Rulesets</h1>
            <p className="text-sm text-gray-500">{rulesets.length} rulesets configured</p>
          </div>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors">
          <Plus className="w-4 h-4" /> Add Ruleset
        </button>
      </div>

      <HowTo />

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" placeholder="Search rulesets..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent" />
      </div>

      {/* Table */}
      <DataTable data={filtered} columns={columns} loading={loading} emptyMessage="No rulesets found." />

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-lg font-semibold">{editingRuleset ? 'Edit Ruleset' : 'Create Ruleset'}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ruleset Code *</label>
                <input type="text" value={form.ruleset_code} disabled={!!editingRuleset}
                  onChange={e => setForm({...form, ruleset_code: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 disabled:bg-gray-100"
                  placeholder="e.g. eligibility_inbound_v1" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ruleset Name *</label>
                <input type="text" value={form.ruleset_name}
                  onChange={e => setForm({...form, ruleset_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400"
                  placeholder="e.g. Eligibility Inbound Validation" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400" rows={3} />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                  className="rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
                <label className="text-sm text-gray-700">Active</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.ruleset_code || !form.ruleset_name}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingRuleset ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rules Assignment Panel */}
      {showRulesPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-lg font-semibold">Manage Rules</h2>
                <p className="text-sm text-gray-500">{showRulesPanel.ruleset_name}</p>
              </div>
              <button onClick={() => setShowRulesPanel(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-hidden flex gap-0">
              {/* Left: Execution Sequence */}
              <div className="w-1/2 border-r flex flex-col">
                <div className="px-4 py-3 bg-amber-50 border-b">
                  <h3 className="text-sm font-semibold text-amber-900">Execution Sequence</h3>
                  <p className="text-xs text-amber-700">{selectedRules.length} rules in order</p>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-1">
                  {selectedRules.length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-8">No rules added yet. Search and add rules from the bank.</p>
                  )}
                  {selectedRules.map((sel, index) => {
                    const rule = allRules.find(r => r.rule_id === sel.rule_id);
                    if (!rule) return null;
                    return (
                      <div key={sel.rule_id} className="flex items-center gap-2 p-2 rounded-lg border border-amber-200 bg-amber-50 group">
                        <span className="w-7 h-7 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="font-medium text-sm truncate">{rule.rule_name}</span>
                            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-[10px] text-amber-700 flex-shrink-0">{rule.rule_type}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0 opacity-60 group-hover:opacity-100">
                          <button onClick={() => moveRule(index, 'up')} disabled={index === 0}
                            className="p-1 hover:bg-amber-200 rounded disabled:opacity-30" title="Move up">
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => moveRule(index, 'down')} disabled={index === selectedRules.length - 1}
                            className="p-1 hover:bg-amber-200 rounded disabled:opacity-30" title="Move down">
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => removeRule(sel.rule_id)}
                            className="p-1 hover:bg-red-100 rounded text-red-500" title="Remove">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Rules Bank */}
              <div className="w-1/2 flex flex-col">
                <div className="px-4 py-3 bg-gray-50 border-b">
                  <h3 className="text-sm font-semibold text-gray-700">Rules Bank</h3>
                  <div className="relative mt-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input type="text" placeholder="Search by name, code, or type..."
                      value={ruleSearch} onChange={e => setRuleSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3 space-y-1">
                  {filteredBankRules.length === 0 && (
                    <p className="text-gray-400 text-sm text-center py-8">
                      {ruleSearch ? 'No matching rules found.' : 'All rules have been added.'}
                    </p>
                  )}
                  {filteredBankRules.map(rule => (
                    <div key={rule.rule_id}
                      onClick={() => addRule(rule.rule_id)}
                      className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors group">
                      <div className="w-6 h-6 rounded-full border-2 border-dashed border-gray-300 group-hover:border-amber-400 flex items-center justify-center flex-shrink-0">
                        <Plus className="w-3 h-3 text-gray-400 group-hover:text-amber-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm truncate">{rule.rule_name}</span>
                          <span className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-600 flex-shrink-0">{rule.rule_type}</span>
                          <span className={`px-1.5 py-0.5 rounded text-[10px] flex-shrink-0 ${
                            rule.severity === 'error' ? 'bg-red-100 text-red-700' :
                            rule.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                          }`}>{rule.severity}</span>
                        </div>
                        {rule.description && <p className="text-xs text-gray-400 mt-0.5 truncate">{rule.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowRulesPanel(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={saveRuleAssignment} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Save ({selectedRules.length} rules)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
